// QR Kiosk v2 — KV2-7 action strip + Hold/Done sheets. Buttons render STRICTLY from
// renderableActions(tk) (state ∩ allowedActions). Start/Resume = direct dispatch with
// an inline error above the strip and an auto-resync on 400 (state drift). Hold/Done =
// the shared KioskSheet (Round-6: portaled + keyboard-safe, so the reason/note textarea
// AND the sheet buttons stay reachable while typing). A 400 in any sheet closes it and
// surfaces the "task changed — refreshed" banner on the strip; other rejections stay
// in-sheet. Tokens only; no MUI/dt/hex.
import React, { useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import useTranslation from "../../../shared/hooks/useTranslation";
import { ServiceReportFileTypeCode } from "../../models/serviceReportsModels";
import { useKioskFiles } from "../reports/useKioskFiles";
import { Glyph, GD } from "../shared/glyphs";
import { usePressed } from "../shared/usePressed";
import { ErrorCallout } from "../shared/ErrorCallout";
import { KioskSheet } from "../shared/KioskSheet";
import { KioskTaskDetail } from "./kioskTasksModels";
import { filesToPhotos, renderableActions } from "./taskUtils";
import {
  clearActionError,
  doneKioskTask,
  getKioskTask,
  holdKioskTask,
  resumeKioskTask,
  startKioskTask,
} from "./kioskTasksSlice";
import { TaskPhotoTiles } from "./TaskBits";

const MAX_PHOTOS = 3;

// ── Action button (designer TaskAction) ──
function ActionButton({
  label, glyph, primary, tone = "--tier-accent", disabled, onClick,
}: {
  label: string; glyph?: string; primary?: boolean; tone?: string; disabled?: boolean; onClick: () => void;
}) {
  const { pressHandlers, pressStyle } = usePressed();
  const bg = primary
    ? tone === "--tier-accent"
      ? "var(--tier-accent)"
      : `color-mix(in oklab, var(${tone}) 82%, var(--ink))`
    : "var(--card)";
  return (
    <button
      type="button"
      {...pressHandlers}
      disabled={disabled}
      onClick={onClick}
      style={{
        flex: 1, height: 54, borderRadius: 12, fontFamily: "inherit", fontSize: 15, fontWeight: 700,
        display: "flex", alignItems: "center", justifyContent: "center", gap: 9, cursor: "pointer",
        border: primary ? "none" : "1px solid var(--border)", background: bg,
        color: primary ? "var(--tier-accent-fg)" : "var(--ink-2)", opacity: disabled ? 0.6 : 1, ...pressStyle,
      }}
    >
      {glyph && <Glyph d={glyph} size={18} />}
      {label}
    </button>
  );
}

const sheetTextarea: React.CSSProperties = {
  width: "100%", marginTop: 12, minHeight: 88, borderRadius: 11, border: "1px solid var(--border)",
  background: "var(--bg)", padding: "11px 13px", fontSize: 14, fontFamily: "inherit", color: "var(--ink)",
  outline: "none", resize: "none", lineHeight: 1.5, boxSizing: "border-box", colorScheme: "light",
};
const requiredLine: React.CSSProperties = {
  marginTop: 6, fontSize: 11.5, fontWeight: 600, color: "color-mix(in oklab, var(--red) 80%, var(--ink))",
};
const cancelBtn = (submitting: boolean): React.CSSProperties => ({
  flex: 1, height: 48, borderRadius: 11, border: "1px solid var(--border)", background: "var(--bg)",
  color: "var(--ink-2)", fontFamily: "inherit", fontSize: 14.5, fontWeight: 600, cursor: "pointer", opacity: submitting ? 0.6 : 1,
});

interface SheetProps {
  tk: KioskTaskDetail;
  number: string;
  open: boolean;
  onClose: () => void;
  onConflict: () => void;
}

// ── Hold sheet ──
function HoldSheet({ tk, number, open, onClose, onConflict }: SheetProps) {
  const dispatch = useAppDispatch();
  const { translate } = useTranslation("public");
  const { translate: tCommon } = useTranslation("common");
  const acting = useAppSelector((s) => s.kioskTasks.acting);
  const actionError = useAppSelector((s) => s.kioskTasks.actionError);
  const [reason, setReason] = useState("");
  const [showReq, setShowReq] = useState(false);
  const submitting = acting === "hold";

  const doClose = () => {
    if (submitting) return; // in-flight guard blocks close
    setShowReq(false);
    dispatch(clearActionError());
    onClose();
  };
  const submit = async () => {
    if (reason.trim().length === 0) { setShowReq(true); return; } // submit stays enabled (KV2-3 discipline)
    const res = await dispatch(holdKioskTask({ id: tk.id, number, reason: reason.trim() }));
    if (holdKioskTask.fulfilled.match(res)) { setReason(""); setShowReq(false); onClose(); }
    else if (res.payload?.status === 400) { onConflict(); setReason(""); setShowReq(false); onClose(); }
  };
  const err = actionError && actionError.action === "hold";

  return (
    <KioskSheet open={open} onRequestClose={doClose} title={translate("kioskV2TaskHoldTitle")}>
      <textarea autoFocus value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} placeholder={translate("kioskV2TaskHoldReasonPlaceholder")} style={sheetTextarea} />
      {showReq && reason.trim().length === 0 && <div style={requiredLine}>{translate("kioskV2TaskHoldReasonRequired")}</div>}
      {err && <div style={{ marginTop: 12 }}><ErrorCallout title={translate("kioskV2TaskActionFailed")} hint={actionError!.message} /></div>}
      <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
        <button type="button" disabled={submitting} onClick={doClose} style={cancelBtn(submitting)}>{tCommon("buttons.cancel")}</button>
        <button
          type="button" disabled={submitting} onClick={submit}
          style={{
            flex: 1, height: 48, borderRadius: 11, border: "none",
            background: "color-mix(in oklab, var(--amber) 82%, var(--ink))", color: "var(--tier-accent-fg)",
            fontFamily: "inherit", fontSize: 14.5, fontWeight: 700, cursor: "pointer", opacity: submitting ? 0.6 : 1,
          }}
        >
          {translate("kioskV2TaskHold")}
        </button>
      </div>
    </KioskSheet>
  );
}

// ── Done sheet ──
function DoneSheet({ tk, number, open, onClose, onConflict }: SheetProps) {
  const dispatch = useAppDispatch();
  const { translate } = useTranslation("public");
  const { translate: tCommon } = useTranslation("common");
  const acting = useAppSelector((s) => s.kioskTasks.acting);
  const actionError = useAppSelector((s) => s.kioskTasks.actionError);
  const [note, setNote] = useState("");
  const [showReq, setShowReq] = useState(false);
  const { files, addFiles, removeFile, rejected, resetFiles } = useKioskFiles(
    ServiceReportFileTypeCode.reportDocument, undefined, { maxFiles: MAX_PHOTOS, imagesOnly: true }
  );
  const submitting = acting === "done";

  const doClose = () => {
    if (submitting) return;
    setShowReq(false);
    dispatch(clearActionError());
    onClose();
  };
  const submit = async () => {
    if (note.trim().length === 0) { setShowReq(true); return; }
    const res = await dispatch(doneKioskTask({ id: tk.id, number, completionNote: note.trim(), photos: filesToPhotos(files) }));
    if (doneKioskTask.fulfilled.match(res)) { setNote(""); resetFiles([]); setShowReq(false); onClose(); }
    else if (res.payload?.status === 400) { onConflict(); setNote(""); resetFiles([]); setShowReq(false); onClose(); }
  };
  const err = actionError && actionError.action === "done";

  return (
    <KioskSheet open={open} onRequestClose={doClose} title={translate("kioskV2TaskDoneTitle")}>
      <div style={{ fontSize: 12.5, color: "var(--muted)", marginTop: 6, lineHeight: 1.5 }}>{translate("kioskV2TaskDoneHint")}</div>
      <textarea autoFocus value={note} onChange={(e) => setNote(e.target.value)} maxLength={4000} placeholder={translate("kioskV2TaskDoneNotePlaceholder")} style={sheetTextarea} />
      {showReq && note.trim().length === 0 && <div style={requiredLine}>{translate("kioskV2TaskDoneNoteRequired")}</div>}
      <div style={{ marginTop: 12 }}>
        <TaskPhotoTiles files={files} onAdd={addFiles} onRemove={removeFile} rejectedName={rejected?.name ?? null} inputId="task-done-photo" max={MAX_PHOTOS} />
      </div>
      {err && (
        <div style={{ marginTop: 12 }}>
          <ErrorCallout
            title={actionError!.status === 429 ? translate("kioskV2TaskTooMany") : translate("kioskV2TaskActionFailed")}
            hint={actionError!.status === 429 ? undefined : actionError!.message}
          />
        </div>
      )}
      <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
        <button type="button" disabled={submitting} onClick={doClose} style={cancelBtn(submitting)}>{tCommon("buttons.cancel")}</button>
        <button
          type="button" disabled={submitting} onClick={submit}
          style={{
            flex: 1, height: 48, borderRadius: 11, border: "none",
            background: "color-mix(in oklab, var(--green) 82%, var(--ink))", color: "var(--tier-accent-fg)",
            fontFamily: "inherit", fontSize: 14.5, fontWeight: 700, cursor: "pointer", opacity: submitting ? 0.6 : 1,
          }}
        >
          {translate("kioskV2TaskDone")}
        </button>
      </div>
    </KioskSheet>
  );
}

export interface TaskActionStripProps {
  tk: KioskTaskDetail;
  number: string;
}

export function TaskActionStrip({ tk, number }: TaskActionStripProps) {
  const dispatch = useAppDispatch();
  const { translate } = useTranslation("public");
  const acting = useAppSelector((s) => s.kioskTasks.acting);
  const [holdOpen, setHoldOpen] = useState(false);
  const [doneOpen, setDoneOpen] = useState(false);
  const [inlineError, setInlineError] = useState<{ message?: string; conflict: boolean } | null>(null);

  const actions = renderableActions(tk);
  if (actions.length === 0) return null;

  const busy = acting != null;
  const conflict = () => { setInlineError({ conflict: true }); dispatch(getKioskTask({ number })); };
  const handleReject = (status?: number, message?: string) => {
    if (status === 400) conflict();
    else setInlineError({ message, conflict: false });
  };
  const runStart = async () => {
    setInlineError(null);
    const res = await dispatch(startKioskTask({ id: tk.id, number }));
    if (startKioskTask.rejected.match(res)) handleReject(res.payload?.status, res.payload?.message);
  };
  const runResume = async () => {
    setInlineError(null);
    const res = await dispatch(resumeKioskTask({ id: tk.id, number }));
    if (resumeKioskTask.rejected.match(res)) handleReject(res.payload?.status, res.payload?.message);
  };
  const openSheet = (setter: (v: boolean) => void) => { setInlineError(null); setter(true); };

  return (
    <>
      {inlineError && (
        <ErrorCallout
          title={translate("kioskV2TaskActionFailed")}
          hint={inlineError.conflict ? translate("kioskV2TaskActionConflict") : inlineError.message}
        />
      )}
      <div style={{ display: "flex", gap: 9, margin: "12px 14px 0", flexShrink: 0 }}>
        {actions.includes("start") && <ActionButton label={translate("kioskV2TaskStart")} glyph={GD.chev} primary disabled={busy} onClick={runStart} />}
        {actions.includes("hold") && <ActionButton label={translate("kioskV2TaskHold")} glyph={GD.hist} disabled={busy} onClick={() => openSheet(setHoldOpen)} />}
        {actions.includes("done") && <ActionButton label={translate("kioskV2TaskDone")} glyph={GD.check} primary tone="--green" disabled={busy} onClick={() => openSheet(setDoneOpen)} />}
        {actions.includes("resume") && <ActionButton label={translate("kioskV2TaskResume")} glyph={GD.chev} primary disabled={busy} onClick={runResume} />}
      </div>
      <HoldSheet tk={tk} number={number} open={holdOpen} onClose={() => setHoldOpen(false)} onConflict={conflict} />
      <DoneSheet tk={tk} number={number} open={doneOpen} onClose={() => setDoneOpen(false)} onConflict={conflict} />
    </>
  );
}

export default TaskActionStrip;
